// SCR-STAFF-511 — چارت دندان (AP-CLN-05؛ US-CLN-113، US-CLN-167): شماره‌گذاری FDI در چهار ربع (دائمی و شیری)، وضعیت هر دندان و سطح،
// درمان برنامه‌ریزی‌شده و انجام‌شده با پیوند به مرحله طرح درمان و خدمت CAT، و برای خدمت بیمه‌ای تامین: انتخاب فقط از فهرست تامین و نمایش
// نتیجه بررسی قواعد («قابل پرداخت» یا علت) کنار هر قلم (BR-CLN-159). هیچ رشته فارسی Hardcode نیست و همه متن‌ها از i18n می‌آیند.
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type ClnDentistServiceType,
  type ClnToothChartView,
  type ClnToothCondition,
  type ClnToothSurface,
  type ClnToothTreatmentView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, toJalaliDateTime, useDisplaySettings } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { clnKeys, errorCode, num } from './common';

/** چارت دندان (US-CLN-113، US-CLN-167). */
export const SCR_STAFF_511 = 'SCR-STAFF-511';

/** بیمه‌گرهای دارای فهرست و قواعد دندانپزشکی (فعلاً فقط تامین — §۴.۵ سند ۱.۹.۹). */
export const DENTAL_INSURERS = ['TAMIN'] as const;

const SURFACES: readonly ClnToothSurface[] = ['Whole', 'Occlusal', 'Mesial', 'Distal', 'Buccal', 'Lingual', 'Root'];
const CONDITIONS: readonly ClnToothCondition[] = ['Healthy', 'Caries', 'Filled', 'Extracted', 'Implant', 'Crown', 'RootCanal', 'Fractured', 'Unerupted'];

/** رنگ وضعیت روی چارت (فقط نشانه دیداری؛ متن وضعیت همیشه در برگه دندان است). */
const CONDITION_TONE: Record<ClnToothCondition, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  Healthy: 'success',
  Caries: 'error',
  Filled: 'info',
  Extracted: 'default',
  Implant: 'info',
  Crown: 'info',
  RootCanal: 'warning',
  Fractured: 'error',
  Unerupted: 'default',
};

export const dentalKeys = {
  chart: (tenantId: string | undefined, patientId: string) => ['clinical', tenantId, 'patient', patientId, 'tooth-chart'] as const,
  insurerServices: (tenantId: string | undefined, patientId: string, insurer: string, serviceType: string, fdi: string) =>
    ['clinical', tenantId, 'patient', patientId, 'dental-services', insurer, serviceType, fdi] as const,
};

/** ربع‌های FDI به ترتیب نمایش (ISO 3950): بالا راست، بالا چپ، پایین چپ، پایین راست. */
export function quadrants(teeth: readonly string[]): string[][] {
  const groups = new Map<string, string[]>();
  for (const fdi of teeth) {
    const quadrant = fdi.slice(0, 1);
    groups.set(quadrant, [...(groups.get(quadrant) ?? []), fdi]);
  }
  // ربع راست از وسط به بیرون خوانده می‌شود، پس برای نمایش برعکس می‌شود (۱۸…۱۱ | ۲۱…۲۸).
  return [...groups.entries()]
    .sort(([left], [right]) => left.localeCompare(right, 'en'))
    .map(([quadrant, list]) => {
      const sorted = [...list].sort((left, right) => left.localeCompare(right, 'en'));
      return quadrant === '1' || quadrant === '3' || quadrant === '5' || quadrant === '7' ? [...sorted].reverse() : sorted;
    });
}

interface ConditionDraft {
  surface: ClnToothSurface;
  condition: ClnToothCondition | '';
  note: string;
}

const emptyDraft: ConditionDraft = { surface: 'Whole', condition: '', note: '' };

export function ToothChartPanel({ patientId, encounterId }: { patientId: string; encounterId?: string }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const { timeZone } = useDisplaySettings();
  const conditionKey = useIdempotencyKey();
  const treatmentKey = useIdempotencyKey();
  const tenantId = session?.tenantId;
  const path = { params: { path: { patientId } } };
  const canManage = access.accessMode === 'Full' && access.can('cln.plan.manage');

  const [dentition, setDentition] = useState<'permanent' | 'primary'>('permanent');
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<ConditionDraft>(emptyDraft);
  const [insurer, setInsurer] = useState<string>('');
  const [serviceType, setServiceType] = useState<ClnDentistServiceType>('Dental');
  const [insurerCode, setInsurerCode] = useState('');
  const [title, setTitle] = useState('');

  const chart = useQuery({
    queryKey: dentalKeys.chart(tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/tooth-chart', path)),
  });

  const services = useQuery({
    queryKey: dentalKeys.insurerServices(tenantId, patientId, insurer, serviceType, selected ?? ''),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/clinical/patients/{patientId}/tooth-chart/insurer-services', {
          params: { path: { patientId }, query: { insurer, serviceType, fdi: serviceType === 'Dental' ? (selected ?? undefined) : undefined } },
        }),
      ),
    enabled: Boolean(insurer) && (serviceType === 'HealthCare' || Boolean(selected)),
    retry: false,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: dentalKeys.chart(tenantId, patientId) });
    void queryClient.invalidateQueries({ queryKey: clnKeys.patient(tenantId, patientId) });
  };

  const setCondition = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/clinical/patients/{patientId}/tooth-chart/conditions', {
          params: { path: { patientId } },
          headers: { 'Idempotency-Key': conditionKey.current() },
          body: { fdi: selected ?? '', surface: draft.surface, condition: (draft.condition || 'Healthy') as ClnToothCondition, note: draft.note.trim() || null, encounterId: encounterId ?? null },
        }),
      ),
    onSettled: (_data, error) => conditionKey.settle(error ?? undefined),
    onSuccess: () => {
      setDraft((current) => ({ ...current, note: '' }));
      refresh();
    },
  });

  const addTreatment = useMutation({
    mutationFn: (body: { status: 'Planned' | 'Done' }) =>
      unwrap(
        tenantApi.POST('/api/v1/clinical/patients/{patientId}/tooth-chart/treatments', {
          params: { path: { patientId } },
          headers: { 'Idempotency-Key': treatmentKey.current() },
          body: {
            fdi: selected ?? '',
            surface: draft.surface,
            status: body.status,
            serviceId: null,
            planStageId: null,
            title: title.trim() || null,
            note: null,
            insurer: insurer || null,
            insurerCode: insurer ? insurerCode || null : null,
            encounterId: encounterId ?? null,
          },
        }),
      ),
    onSettled: (_data, error) => treatmentKey.settle(error ?? undefined),
    onSuccess: () => {
      setTitle('');
      setInsurerCode('');
      refresh();
    },
  });

  const act = useMutation({
    mutationFn: (input: { id: string; action: 'complete' | 'cancel' | 'recheck-rules' }) => {
      const params = { params: { path: { patientId, treatmentId: input.id } } };

      if (input.action === 'cancel') {
        return unwrap(tenantApi.POST('/api/v1/clinical/patients/{patientId}/tooth-chart/treatments/{treatmentId}:cancel', { ...params, body: { reason: null } }));
      }

      if (input.action === 'recheck-rules') {
        return unwrap(tenantApi.POST('/api/v1/clinical/patients/{patientId}/tooth-chart/treatments/{treatmentId}:recheck-rules', params));
      }

      return unwrap(tenantApi.POST('/api/v1/clinical/patients/{patientId}/tooth-chart/treatments/{treatmentId}:complete', { ...params, body: { encounterId: encounterId ?? null } }));
    },
    onSuccess: refresh,
  });

  const data: ClnToothChartView | undefined = chart.data;
  const teeth = dentition === 'permanent' ? (data?.permanentTeeth ?? []) : (data?.primaryTeeth ?? []);
  const conditions = data?.conditions ?? [];
  const treatments = data?.treatments ?? [];
  const selectedConditions = conditions.filter((row) => row.fdi === selected);
  const selectedTreatments = treatments.filter((row) => row.fdi === selected);
  const toothCondition = (fdi: string) => conditions.find((row) => row.fdi === fdi && row.surface === 'Whole') ?? conditions.find((row) => row.fdi === fdi);
  const unavailable = services.data?.unavailable === true;

  if (chart.error) {
    return <ErrorState error={chart.error} />;
  }

  return (
    <Stack spacing={2} data-screen-id={SCR_STAFF_511} data-testid="cln-tooth-chart">
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" component="h3" sx={{ flexGrow: 1 }}>
            {t('cln:dental.title')}
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={dentition}
            onChange={(_event, value: 'permanent' | 'primary' | null) => value && setDentition(value)}
            aria-label={t('cln:dental.dentition')}
          >
            <ToggleButton value="permanent" data-testid="cln-dentition-permanent">
              {t('cln:dental.permanent')}
            </ToggleButton>
            <ToggleButton value="primary" data-testid="cln-dentition-primary">
              {t('cln:dental.primary')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Stack spacing={0.5} data-testid="cln-fdi-chart">
          {quadrants(teeth).map((row, index) => (
            <Stack key={row.join('-')} direction="row" spacing={0.5} useFlexGap flexWrap="wrap" data-testid={`cln-fdi-quadrant-${faDigits(index + 1)}`}>
              {row.map((fdi) => {
                const found = toothCondition(fdi);
                return (
                  <Chip
                    key={fdi}
                    size="small"
                    component="button"
                    clickable
                    aria-pressed={selected === fdi}
                    color={found ? CONDITION_TONE[found.condition] : 'default'}
                    variant={selected === fdi ? 'filled' : 'outlined'}
                    label={faDigits(fdi)}
                    onClick={() => setSelected(fdi)}
                    data-testid={`cln-tooth-${fdi}`}
                    data-condition={found?.condition ?? 'None'}
                  />
                );
              })}
            </Stack>
          ))}
        </Stack>
        {!selected && (
          <Typography variant="caption" color="text.secondary">
            {t('cln:dental.pick_tooth')}
          </Typography>
        )}
      </Paper>

      {selected && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="cln-tooth-detail">
              <Typography variant="subtitle2" component="h4" sx={{ mb: 1 }}>
                {t('cln:dental.tooth', { fdi: faDigits(selected) })}
              </Typography>
              <Stack spacing={1}>
                {selectedConditions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('cln:dental.no_condition')}
                  </Typography>
                ) : (
                  selectedConditions.map((row) => (
                    <Stack key={row.id} direction="row" spacing={1} sx={{ alignItems: 'center' }} data-testid="cln-tooth-condition" data-surface={row.surface}>
                      <Chip size="small" color={CONDITION_TONE[row.condition]} label={t(`cln:dental.condition.${row.condition}`)} />
                      <Typography variant="body2">{t(`cln:dental.surface.${row.surface}`)}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                        {row.note ?? ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {toJalaliDateTime(row.recordedAt, { timeZone })}
                      </Typography>
                    </Stack>
                  ))
                )}
                {canManage && (
                  <Stack spacing={1} data-testid="cln-tooth-condition-form">
                    <Stack direction="row" spacing={1}>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        label={t('cln:dental.surface_label')}
                        value={draft.surface}
                        onChange={(event) => setDraft({ ...draft, surface: event.target.value as ClnToothSurface })}
                        slotProps={{ htmlInput: { 'data-testid': 'cln-tooth-surface' } }}
                      >
                        {SURFACES.map((surface) => (
                          <MenuItem key={surface} value={surface}>
                            {t(`cln:dental.surface.${surface}`)}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        label={t('cln:dental.condition_label')}
                        value={draft.condition}
                        onChange={(event) => setDraft({ ...draft, condition: event.target.value as ClnToothCondition })}
                        slotProps={{ htmlInput: { 'data-testid': 'cln-tooth-condition-value' } }}
                      >
                        {CONDITIONS.map((condition) => (
                          <MenuItem key={condition} value={condition}>
                            {t(`cln:dental.condition.${condition}`)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                    <TextField
                      size="small"
                      label={t('cln:dental.note')}
                      value={draft.note}
                      onChange={(event) => setDraft({ ...draft, note: event.target.value })}
                      slotProps={{ htmlInput: { 'data-testid': 'cln-tooth-note' } }}
                    />
                    <Box>
                      <Button size="small" variant="outlined" disabled={!draft.condition || setCondition.isPending} onClick={() => setCondition.mutate()} data-testid="cln-tooth-condition-save">
                        {t('cln:dental.save_condition')}
                      </Button>
                    </Box>
                    {setCondition.error ? <ErrorState error={setCondition.error} compact /> : null}
                  </Stack>
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="cln-dental-treatments">
              <Typography variant="subtitle2" component="h4" sx={{ mb: 1 }}>
                {t('cln:dental.treatments')}
              </Typography>
              <Stack spacing={1}>
                {selectedTreatments.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('cln:dental.no_treatment')}
                  </Typography>
                ) : (
                  selectedTreatments.map((row) => <TreatmentRow key={row.id} row={row} canManage={canManage} onAct={(action) => act.mutate({ id: row.id, action })} />)
                )}
                {act.error ? <ErrorState error={act.error} compact /> : null}

                {canManage && (
                  <Stack spacing={1} data-testid="cln-dental-treatment-form">
                    <Stack direction="row" spacing={1}>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        label={t('cln:dental.insurer')}
                        value={insurer}
                        onChange={(event) => {
                          setInsurer(event.target.value);
                          setInsurerCode('');
                        }}
                        slotProps={{ htmlInput: { 'data-testid': 'cln-dental-insurer' } }}
                      >
                        <MenuItem value="">{t('cln:dental.self_pay')}</MenuItem>
                        {DENTAL_INSURERS.map((code) => (
                          <MenuItem key={code} value={code}>
                            {t(`cln:dental.insurer_name.${code}`)}
                          </MenuItem>
                        ))}
                      </TextField>
                      {insurer ? (
                        <TextField
                          select
                          size="small"
                          fullWidth
                          label={t('cln:dental.service_type')}
                          value={serviceType}
                          onChange={(event) => setServiceType(event.target.value as ClnDentistServiceType)}
                          slotProps={{ htmlInput: { 'data-testid': 'cln-dental-service-type' } }}
                        >
                          <MenuItem value="Dental">{t('cln:dental.service_type_dental')}</MenuItem>
                          <MenuItem value="HealthCare">{t('cln:dental.service_type_health')}</MenuItem>
                        </TextField>
                      ) : (
                        <TextField
                          size="small"
                          fullWidth
                          label={t('cln:dental.treatment_title')}
                          value={title}
                          onChange={(event) => setTitle(event.target.value)}
                          slotProps={{ htmlInput: { 'data-testid': 'cln-dental-title' } }}
                        />
                      )}
                    </Stack>

                    {insurer ? (
                      unavailable ? (
                        <Alert severity="warning" data-testid="cln-dental-unavailable">
                          {t('cln:dental.insurer_unavailable', { reason: services.data?.reason ?? '' })}
                        </Alert>
                      ) : (
                        <TextField
                          select
                          size="small"
                          fullWidth
                          label={t('cln:dental.insurer_service')}
                          helperText={t('cln:dental.insurer_service_hint')}
                          value={insurerCode}
                          onChange={(event) => setInsurerCode(event.target.value)}
                          disabled={services.isPending}
                          slotProps={{ htmlInput: { 'data-testid': 'cln-dental-service' } }}
                        >
                          {(services.data?.items ?? []).map((item) => (
                            <MenuItem key={item.code} value={item.code}>
                              {item.name ?? item.code}
                              {item.price ? ` — ${faDigits(num(item.price.amount) ?? 0)}` : ''}
                            </MenuItem>
                          ))}
                        </TextField>
                      )
                    ) : null}

                    <Box>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={addTreatment.isPending || (Boolean(insurer) && (unavailable || !insurerCode))}
                        onClick={() => addTreatment.mutate({ status: 'Planned' })}
                        data-testid="cln-dental-add"
                      >
                        {t('cln:dental.add_treatment')}
                      </Button>
                    </Box>
                    {addTreatment.error ? (
                      errorCode(addTreatment.error) === 'PHX-CLN-0307' ? (
                        <Alert severity="error" data-testid="cln-dental-not-payable">
                          {t('cln:dental.not_payable')}
                        </Alert>
                      ) : (
                        <ErrorState error={addTreatment.error} compact />
                      )
                    ) : null}
                  </Stack>
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Stack>
  );
}

function TreatmentRow({ row, canManage, onAct }: { row: ClnToothTreatmentView; canManage: boolean; onAct: (action: 'complete' | 'cancel' | 'recheck-rules') => void }) {
  const { t } = useTranslation('cln');
  const { timeZone } = useDisplaySettings();
  const insured = Boolean(row.insurer);
  const payable = row.insurerPayable;
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }} useFlexGap flexWrap="wrap" data-testid="cln-dental-treatment" data-status={row.status} data-payable={payable === null || payable === undefined ? 'Unchecked' : String(payable)}>
      <Chip size="small" variant="outlined" label={t(`cln:dental.status.${row.status}`)} />
      <Typography variant="body2" sx={{ flexGrow: 1 }}>
        {row.serviceName ?? row.title ?? row.insurerCode ?? '—'}
      </Typography>
      {insured && (
        <Chip
          size="small"
          color={payable === true ? 'success' : payable === false ? 'error' : 'warning'}
          label={payable === true ? t('cln:dental.payable') : payable === false ? t('cln:dental.unpayable') : t('cln:dental.unchecked')}
          data-testid="cln-dental-payable"
        />
      )}
      {row.insurerReason && (
        <Typography variant="caption" color="error" data-testid="cln-dental-reason">
          {row.insurerReason}
        </Typography>
      )}
      {row.insurerPrice && <Typography variant="caption">{faDigits(num(row.insurerPrice.amount) ?? 0)}</Typography>}
      {row.completedAt && <Typography variant="caption" color="text.secondary">{toJalaliDateTime(row.completedAt, { timeZone })}</Typography>}
      {canManage && row.status === 'Planned' && (
        <>
          <Button size="small" onClick={() => onAct('complete')} data-testid="cln-dental-complete">
            {t('cln:dental.complete')}
          </Button>
          <Button size="small" color="warning" onClick={() => onAct('cancel')} data-testid="cln-dental-cancel">
            {t('cln:dental.cancel')}
          </Button>
        </>
      )}
      {canManage && insured && payable !== true && (
        <Button size="small" onClick={() => onAct('recheck-rules')} data-testid="cln-dental-recheck">
          {t('cln:dental.recheck')}
        </Button>
      )}
    </Stack>
  );
}
